import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export type EditUserQuotaUser = {
  name?: string;
  email?: string;
  tokenQuota?: string;
  [key: string]: unknown;
};

type EditUserQuotaProps = {
  open: boolean;
  onClose: () => void;
  user: EditUserQuotaUser | null;
  onSaveSuccess: () => void;
  updateQuota: (email: string | undefined, updatedQuotaValue: number, isGlobal?: boolean) => Promise<boolean>;
  loading?: boolean;
  isGlobal?: boolean;
};

export default function EditUserQuota({
  open,
  onClose,
  user,
  onSaveSuccess,
  updateQuota,
  loading: externalLoading = false,
  isGlobal = false,
}: EditUserQuotaProps) {
  const [quota, setQuota] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && user) {
      setQuota(user.tokenQuota ?? '');
      setError(null);
    }
  }, [open, user]);

  const handleSave = async () => {
    if (!isGlobal && !user?.email?.trim()) return;
    const value = quota.trim();
    if (!value) {
      setError('Please enter a quota value.');
      return;
    }
    const num = Number(value);
    if (Number.isNaN(num) || num < 0) {
      setError('Please enter a valid number.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const success = await updateQuota(isGlobal ? undefined : user?.email?.trim(), num, isGlobal);
      if (success) {
        onSaveSuccess();
      } else {
        setError('Failed to update quota. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const isLoading = saving || externalLoading;

  if (!isGlobal && !user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Token Quota</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!isGlobal && (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  User Name
                </Typography>
                <Typography variant="body1">{user?.name ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Email ID
                </Typography>
                <Typography variant="body1">{user?.email ?? '—'}</Typography>
              </Box>
            </>
          )}
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Token Quota (K tokens, monthly)"
              value={quota}
              onChange={(e) => setQuota(e.target.value)}
              variant="outlined"
              placeholder="e.g. 100000"
              error={!!error}
              helperText={error}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: -1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: 'info.main' }} />
            <Typography variant="body2" color="text.secondary">
              You are updating the user token quota for a month.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} color="inherit" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
